import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { eventTargetNotInTextbox } from 'donor-elf-web/lib/document-utils';
import CommunicationModel from 'donor-elf-web/models/communication';
import ContactDateModel from 'donor-elf-web/models/contact-date';
import type { ContactLastGiftToOtherFund } from 'donor-elf-web/models/contact-detail';
import EventModel from 'donor-elf-web/models/event';
import LineItemModel from 'donor-elf-web/models/line-item';
import MailchimpEmailSyncModel from 'donor-elf-web/models/mailchimp-email-sync';
import NewsletterContactModel from 'donor-elf-web/models/newsletter-contact';
import NoteModel from 'donor-elf-web/models/note';
import PledgeModel from 'donor-elf-web/models/pledge';
import ReviewItemModel from 'donor-elf-web/models/review-item';
import TaskModel from 'donor-elf-web/models/task';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import type { ContactRouteModel } from 'donor-elf-web/routes/funds/fund/contacts/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';
import AddSomethingModal from 'donor-elf-web/services/add-something-modal';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import FundsService from 'donor-elf-web/services/funds-service';
import JournalService from 'donor-elf-web/services/journal-service';
import PowerMenuService from 'donor-elf-web/services/power-menu';
import TaskService from 'donor-elf-web/services/task-service';

import type RouterService from '@ember/routing/router-service';
import CampaignPledgeModel from 'donor-elf-web/models/campaign-pledge';
import ContactCampaignSummaryModel from 'donor-elf-web/models/contact-campaign-summary';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
const addToJournalValidation = {
  journalId: validatePresence({ presence: true, message: "Can't be blank" }),
};

export default class ContactDetailsController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModal;
  @service declare contactsCache: ContactsCache;
  @service declare fundsService: FundsService;
  @service declare journalService: JournalService;
  @service declare media: any;
  @service declare powerMenu: PowerMenuService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare taskService: TaskService;

  @tracked model!: ContactRouteModel;

  @tracked loading = true;
  @tracked showNextPrevious = false;
  @tracked hasPreviousContact = false;
  @tracked hasNextContact = false;
  @tracked reRender = 0;

  @tracked lineItems: LineItemModel[] = [];
  @tracked gifts: LineItemModel[] = [];
  @tracked notes: NoteModel[] = [];
  @tracked events: EventModel[] = [];
  @tracked dates: ContactDateModel[] = [];
  @tracked pledges: PledgeModel[] = [];
  @tracked tasks: TaskModel[] = [];
  @tracked communications: CommunicationModel[] = [];
  @tracked v2Tasks: V2TaskModel[] = [];
  @tracked reviewItems: ReviewItemModel[] = [];
  @tracked mailchimpEmailSyncs: MailchimpEmailSyncModel[] = [];
  @tracked newsletterContact?: NewsletterContactModel;
  @tracked lastGiftToOtherFunds?: ContactLastGiftToOtherFund[] = [];
  @tracked campaignSummaries: ContactCampaignSummaryModel[] = [];
  @tracked campaignPledges: CampaignPledgeModel[] = [];
  @tracked addToJournal = false;

  setup() {
    this.showNextPrevious = false;
    this.hasPreviousContact = false;
    this.hasNextContact = false;
    this.lineItems = [];
    this.gifts = [];
    this.notes = [];
    this.events = [];
    this.dates = [];
    this.pledges = [];
    this.tasks = [];
    this.communications = [];
    this.v2Tasks = [];
    this.reviewItems = [];
    this.mailchimpEmailSyncs = [];
    this.newsletterContact = undefined;
    this.lastGiftToOtherFunds = [];
    this.campaignSummaries = [];
    this.campaignPledges = [];
    this.addToJournal = false;
    this.closeAddToJournal();

    this.load();
    this.showNextPrevious =
      this.media.isMediumUp &&
      this.contactsCache.nextPreviousContactIds != null;
    if (this.showNextPrevious) {
      let nextPreviousIndex = this.nextPreviousIndex;
      this.hasPreviousContact = nextPreviousIndex > 0;
      this.hasNextContact =
        nextPreviousIndex <
        this.contactsCache.nextPreviousContactIds.length - 1;
    }
    this.powerMenu.prependOptionsFn(
      'contacts-details-prepend',
      this.powerMenuOptionsToPrepend,
    );
    this.powerMenu.appendOptionsFn(
      'contacts-details-append',
      this.powerMenuOptionsToAppend,
    );
  }

  async load() {
    if (!this.abilities.hasAbility('view_contact_details')) {
      return;
    }

    this.loading = true;
    await this.taskService.load();
    let details = await this.store.findRecord(
      'contact-detail',
      this.model.contact.id,
    );
    if (details.id !== this.model.contact.id) {
      // the user already went to another contact page before the data came
      // back so don't process this record since it's for a different contact
      return;
    }
    this.lineItems = details.lines.slice();
    this.gifts = this.lineItems.filter((x) => x.amount > 0);
    this.notes = details.notes.slice();
    this.events = details.events.slice();
    this.dates = details.dates.slice();
    this.pledges = details.pledges.slice();
    this.tasks = details.tasks.slice();
    this.communications = details.communications.slice();
    this.v2Tasks = details.v2Tasks.slice();
    this.reviewItems = details.reviewItems.slice();
    this.newsletterContact = details.newsletterContact;
    this.mailchimpEmailSyncs = details.mailchimpEmailSyncs.slice();
    this.lastGiftToOtherFunds = details.lastGiftToOtherFunds;
    this.campaignSummaries = details.campaignSummaries.slice();
    this.campaignPledges = details.campaignPledges.slice();
    this.loading = false;
  }

  get oneColumnLayout() {
    return this.media.isMediumDown;
  }
  get twoColumnLayout() {
    return !this.oneColumnLayout;
  }

  get actionsMenuItems() {
    let actions = [];
    if (this.abilities.hasAbility('edit_contact')) {
      actions.push({
        label: 'Task',
        powerMenuLabel: 'Add Task',
        svgJar: 'plus',
        onClick: () => {
          this.addSomethingModal.open({
            type: 'task',
          });
        },
      });

      if (
        this.abilities.hasAbility('edit_pledges') ||
        this.abilities.hasAbility('edit_manual_pledges')
      ) {
        actions.push({
          label: 'Pledge',
          powerMenuLabel: 'Add Pledge',
          svgJar: 'plus',
          onClick: () => {
            this.addSomethingModal.open({
              type: 'pledge',
              pledgeOptions: { contactId: this.model.contact.id },
            });
          },
        });
      }

      if (
        this.abilities.hasAbility('edit_contact') &&
        this.journalService.journals.length > 0
      ) {
        actions.push({
          label: 'Journal',
          powerMenuLabel: 'Add to Journal',
          svgJar: 'plus',
          onClick: this.handleAddToJournal,
        });
      }

      actions.push({
        label: 'Note',
        powerMenuLabel: 'Add Note',
        svgJar: 'plus',
        onClick: () => {
          this.router.transitionTo(
            'funds.fund.contacts.contact.edit-note',
            'new',
          );
        },
      });

      actions.push({
        label: 'Date',
        powerMenuLabel: 'Add Date',
        svgJar: 'plus',
        onClick: () => {
          this.router.transitionTo(
            'funds.fund.contacts.contact.dates.edit',
            'add',
          );
        },
      });

      if (this.abilities.hasAbility('edit_transactions')) {
        actions.push({
          label: 'Transaction',
          powerMenuLabel: 'Add Transaction',
          svgJar: 'plus',
          onClick: () => {
            this.router.transitionTo(
              'funds.fund.transactions.edit.enter',
              'add',
              { queryParams: { contactId: this.model.contact.id } },
            );
          },
        });
      }

      actions.push({
        label: 'Merge',
        svgJar: 'code-branch',
        onClick: () => {
          this.router.transitionTo('funds.fund.contacts.contact.merge');
        },
      });

      if (this.model.contact.active) {
        actions.push({
          label: 'Hide',
          svgJar: 'eye-slash',
          onClick: () => {
            this.handleHideContact();
          },
        });
      } else {
        actions.push({
          label: 'Unhide',
          svgJar: 'eye',
          onClick: () => {
            this.handleUnhideContact();
          },
        });
      }

      actions.push({
        label: 'Delete',
        svgJar: 'trash',
        onClick: () => {
          this.router.transitionTo('funds.fund.contacts.contact.delete');
        },
      });
    }
    return actions;
  }

  @action
  templateDidInsert() {
    if (this.showNextPrevious) {
      document.addEventListener('keydown', this.handleDocumentKeyDown);
    }
  }

  @action
  templateWillDestroy() {
    this.powerMenu.removeOptions('contacts-details-prepend');
    this.powerMenu.removeOptions('contacts-details-append');
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  }

  @action
  powerMenuOptionsToPrepend() {
    let options = [];
    if (this.showNextPrevious) {
      if (this.hasNextContact) {
        options.push({
          label: 'Goto Next Contact',
          fn: this.gotoNextContact,
        });
      }
      if (this.hasPreviousContact) {
        options.push({
          label: 'Goto Previous Contact',
          fn: this.gotoPreviousContact,
        });
      }
    }
    return options;
  }

  powerMenuOptionsToAppend() {
    return [
      {
        label: 'Contact Gifts by Pledge',
        route: 'funds.fund.contacts.contact.gifts-by-pledge',
      },
    ];
  }

  @action
  writeFindContactInRubyToClipboard() {
    window.navigator.clipboard.writeText(
      `contact = Contact.find(${this.model.contact.id})`,
    );
  }

  @action
  handleDocumentKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowLeft' && eventTargetNotInTextbox(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.gotoPreviousContact();
    } else if (e.key === 'ArrowRight' && eventTargetNotInTextbox(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.gotoNextContact();
    }
  }

  @action
  gotoPreviousContact() {
    if (this.hasPreviousContact) {
      let previousContactId =
        this.contactsCache.nextPreviousContactIds[this.nextPreviousIndex - 1];
      this.router.transitionTo(
        'funds.fund.contacts.contact',
        previousContactId,
      );
    }
  }

  @action
  gotoNextContact() {
    if (this.hasNextContact) {
      let nextContactId =
        this.contactsCache.nextPreviousContactIds[this.nextPreviousIndex + 1];
      this.router.transitionTo('funds.fund.contacts.contact', nextContactId);
    }
  }

  @action
  handleUnhideContact() {
    this.model.contact.active = true;
    this.model.contact.save();
  }

  @action
  handleHideContact() {
    this.model.contact.active = false;
    this.model.contact.save();
  }

  @action
  handleReviewItemSaved(_item: ReviewItemModel) {
    this.load();
  }

  @action
  handleNewsletterContactCreated(newsletterContact: NewsletterContactModel) {
    this.newsletterContact = newsletterContact;
  }

  @action
  handleNewsletterContactDestroyed(_newsletterContact: NewsletterContactModel) {
    this.newsletterContact = undefined;
  }

  get nextPreviousIndex() {
    if (this.contactsCache.nextPreviousContactIds) {
      return this.contactsCache.nextPreviousContactIds.indexOf(
        this.model.contact.id,
      );
    } else {
      return -1;
    }
  }

  // TODO: move these to their own component
  @tracked savingAddingToJournal = false;
  @tracked addToJournalChangeset?: any;
  @tracked showValidationErrors = false;
  @tracked saveAddToJournalError?: any;

  @action
  handleAddToJournal() {
    this.addToJournal = true;
    this.addToJournalChangeset = new Changeset(
      { journalId: undefined },
      lookupValidator(addToJournalValidation),
      addToJournalValidation,
    );
  }

  get journalsNotCurrentlyIn() {
    let contactId = this.model.contact.id;
    let activeJournalIdsForContact = (
      this.journalService.journalInfosByContactId[contactId] || []
    ).map((x) => x.journal.id);
    return this.journalService.journals.filter((journal) => {
      return activeJournalIdsForContact.indexOf(journal.id) === -1;
    });
  }

  get journalOptions() {
    let { journalsNotCurrentlyIn } = this;
    return this.journalService.sortedJournalInfos
      .filter((journalInfo) => {
        return journalsNotCurrentlyIn.includes(journalInfo.journal);
      })
      .map((journalInfo) => {
        return {
          val: journalInfo.journal.id,
          label: journalInfo.journal.label,
        };
      });
  }

  @action
  async saveAddToJournal() {
    if (this.savingAddingToJournal) {
      return;
    }
    this.savingAddingToJournal = true;
    this.showValidationErrors = false;
    await this.addToJournalChangeset.validate();
    if (this.addToJournalChangeset.get('isValid')) {
      try {
        await this.journalService.addContactToJournal(
          this.addToJournalChangeset.get('journalId'),
          this.model.contact,
        );
        this.closeAddToJournal();
      } catch (error) {
        this.saveAddToJournalError = error;
      }
    } else {
      this.showValidationErrors = true;
    }
    this.savingAddingToJournal = false;
  }

  @action
  closeAddToJournal() {
    this.addToJournal = false;
    this.savingAddingToJournal = false;
    this.addToJournalChangeset = undefined;
    this.showValidationErrors = false;
    this.saveAddToJournalError = undefined;
  }
  // Finish TODO above
}
